import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  AlertTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Card,
  CardContent,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  InputLabel,
  Skeleton,
} from "@mui/material";
import {
  PlayArrow as GenerateIcon,
  ExpandMore as ExpandMoreIcon,
  Receipt as ReceiptIcon,
  TrendingUp as RevenueIcon,
  TrendingDown as ExpenseIcon,
  AccountBalance as TaxIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Download as DownloadIcon,
  Lightbulb as LightbulbIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  HelpOutline as HelpIcon,
  AccountBalanceWallet as WalletIcon,
  Savings as SavingsIcon,
  CalendarMonth as CalendarIcon,
  PriorityHigh as PriorityIcon,
  AttachMoney as MoneyIcon,
  Gavel as ComplianceIcon,
  TipsAndUpdates as TipsIcon,
  Description as InvoiceIcon,
  AccessTime as TimeIcon,
  Analytics as MetricsIcon,
  ShowChart as ChartIcon,
  Percent as PercentIcon,
  BarChart as BarChartIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  Line,
  ComposedChart,
} from "recharts";
import api from "../services/api";

const currentYear = new Date().getFullYear();
// Include current year and previous 3 years
const yearOptions = [
  currentYear,
  currentYear - 1,
  currentYear - 2,
  currentYear - 3,
];

export default function RevenueAccountsPage() {
  const theme = useTheme();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [accountingBasis, setAccountingBasis] = useState("accrual");
  const [loading, setLoading] = useState(false);
  const [accountsData, setAccountsData] = useState(null);
  const [error, setError] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  const generateAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(
        `/accounts/generate?year=${selectedYear}&basis=${accountingBasis}`
      );
      setAccountsData(res.data);
    } catch (err) {
      console.error("Failed to generate accounts:", err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          "Failed to generate accounts. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const migrateCurrencies = async () => {
    setMigrating(true);
    setMigrationResult(null);
    try {
      const res = await api.post("/accounts/migrate-currencies");
      setMigrationResult(res.data);
      // Refresh accounts data after migration
      if (res.data.migrated > 0) {
        await generateAccounts();
      }
    } catch (err) {
      console.error("Failed to migrate currencies:", err);
      setMigrationResult({
        success: false,
        error: err.response?.data?.error || "Migration failed",
      });
    } finally {
      setMigrating(false);
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat("en-IE", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  const SummaryCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <Card sx={{ height: "100%", borderRadius: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette[color].main, 0.1),
            }}
          >
            <Icon sx={{ color: `${color}.main`, fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h5" fontWeight={700} color={`${color}.main`}>
              {formatMoney(value)}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Revenue Accounts
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Generate your annual accounts for Irish Revenue submission. Includes
          Profit & Loss, Corporation Tax computation, and detailed explanations.
        </Typography>
      </Box>

      {/* Controls */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Grid container spacing={3} alignItems="center">
          {/* Year Selection */}
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Financial Year</InputLabel>
              <Select
                value={selectedYear}
                label="Financial Year"
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setAccountsData(null); // Clear old data when year changes
                }}
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Accounting Basis */}
          <Grid item xs={12} sm={5} md={5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ToggleButtonGroup
                value={accountingBasis}
                exclusive
                onChange={(e, value) => {
                  if (value) {
                    setAccountingBasis(value);
                    setAccountsData(null);
                  }
                }}
                size="small"
              >
                <ToggleButton value="accrual" sx={{ px: 2 }}>
                  Accrual
                </ToggleButton>
                <ToggleButton value="cash" sx={{ px: 2 }}>
                  Cash
                </ToggleButton>
              </ToggleButtonGroup>
              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Accounting Basis
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      <strong>Accrual:</strong> Revenue recognized when invoiced
                      (recommended for most companies)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      <strong>Cash:</strong> Revenue recognized when paid (only
                      for cash-basis accounting)
                    </Typography>
                  </Box>
                }
                arrow
              >
                <HelpIcon
                  fontSize="small"
                  sx={{ color: "text.secondary", cursor: "help" }}
                />
              </Tooltip>
            </Stack>
          </Grid>

          {/* Generate Button */}
          <Grid item xs={12} sm={3} md={4}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : accountsData ? (
                  <RefreshIcon />
                ) : (
                  <GenerateIcon />
                )
              }
              onClick={generateAccounts}
              disabled={loading}
              sx={{ height: 48 }}
            >
              {loading
                ? "Generating..."
                : accountsData
                ? "Regenerate"
                : "Generate Accounts"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Skeleton
                  variant="rounded"
                  height={100}
                  sx={{ borderRadius: 3 }}
                />
              </Grid>
            ))}
          </Grid>
          <Skeleton
            variant="rounded"
            height={300}
            sx={{ borderRadius: 3, mb: 3 }}
          />
          <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
        </Box>
      )}

      {/* Results */}
      {accountsData && !loading && (
        <>
          {/* Year & Basis Header */}
          <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h6" color="text.secondary">
              {selectedYear} Accounts
            </Typography>
            <Chip
              label={
                accountingBasis === "accrual" ? "Accrual Basis" : "Cash Basis"
              }
              size="small"
              color="primary"
              variant="outlined"
            />
            <Typography variant="body2" color="text.secondary">
              Generated: {new Date(accountsData.generatedAt).toLocaleString()}
            </Typography>
          </Box>

          {/* Warnings/Alerts */}
          {accountsData.warnings?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {accountsData.warnings.map((warning, idx) => {
                const getSeverity = (type) => {
                  switch (type) {
                    case "success":
                      return "success";
                    case "warning":
                      return "warning";
                    case "caution":
                      return "warning";
                    case "error":
                      return "error";
                    default:
                      return "info";
                  }
                };
                const getIcon = (type) => {
                  switch (type) {
                    case "success":
                      return <CheckIcon />;
                    case "warning":
                      return <WarningIcon />;
                    case "caution":
                      return <WarningIcon />;
                    case "error":
                      return <ErrorIcon />;
                    default:
                      return <InfoIcon />;
                  }
                };
                return (
                  <Alert
                    key={idx}
                    severity={getSeverity(warning.type)}
                    sx={{ mb: 1, borderRadius: 2 }}
                    icon={getIcon(warning.type)}
                  >
                    <AlertTitle>{warning.title}</AlertTitle>
                    {warning.message}
                  </Alert>
                );
              })}
            </Box>
          )}

          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                title="Total Revenue"
                value={
                  accountsData.summary?.revenue ??
                  accountsData.profitAndLoss.revenue
                }
                icon={RevenueIcon}
                color="success"
                subtitle={`${accountsData.counts?.invoices || 0} invoices`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                title="Total Expenses"
                value={
                  accountsData.summary?.expenses ??
                  accountsData.profitAndLoss.expenses
                }
                icon={ExpenseIcon}
                color="error"
                subtitle={`${accountsData.counts?.expenses || 0} receipts`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                title={
                  accountsData.summary?.operatingProfit >= 0
                    ? "Net Profit"
                    : "Net Loss"
                }
                value={Math.abs(
                  accountsData.summary?.operatingProfit ??
                    accountsData.profitAndLoss.netProfitBeforeTax
                )}
                icon={ReceiptIcon}
                color={
                  accountsData.summary?.operatingProfit >= 0
                    ? "primary"
                    : "error"
                }
                subtitle="Before tax"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                title="Corporation Tax"
                value={accountsData.corporationTax?.taxDue ?? 0}
                icon={TaxIcon}
                color="warning"
                subtitle={accountsData.corporationTax?.ratePercent || "@ 12.5%"}
              />
            </Grid>
          </Grid>

          {/* Bank Balance Section */}
          {accountsData.bankBalance?.connected && (
            <Paper
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.info.main,
                  0.1
                )} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <WalletIcon color="info" />
                <Typography variant="h6" fontWeight={700}>
                  Bank Account Balance
                </Typography>
                {accountsData.bankBalance.institutionName && (
                  <Chip
                    label={accountsData.bankBalance.institutionName}
                    size="small"
                    variant="outlined"
                    color="info"
                  />
                )}
              </Box>
              <Grid container spacing={3}>
                {accountsData.bankBalance.accounts?.map((account, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          {account.ownerName || "Business Account"}
                        </Typography>
                        <Typography
                          variant="h4"
                          fontWeight={700}
                          color={
                            account.amount >= 0 ? "success.main" : "error.main"
                          }
                        >
                          {formatMoney(account.amount)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {account.iban
                            ? `****${account.iban.slice(-4)}`
                            : "Account"}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                {accountsData.bankBalance.totalBalance !== null &&
                  accountsData.bankBalance.accounts?.length > 1 && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Card
                        sx={{
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        }}
                      >
                        <CardContent>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            Total Balance
                          </Typography>
                          <Typography
                            variant="h4"
                            fontWeight={700}
                            color="primary.main"
                          >
                            {formatMoney(accountsData.bankBalance.totalBalance)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            All accounts combined
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
              </Grid>
            </Paper>
          )}

          {/* Monthly Revenue vs Expenses Chart */}
          {accountsData.monthlyBreakdown && (
            <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 2 }}
              >
                <BarChartIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Monthly Revenue vs Expenses - {selectedYear}
                </Typography>
              </Stack>

              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart
                  data={accountsData.monthlyBreakdown}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) =>
                      v >= 1000 ? `€${(v / 1000).toFixed(0)}k` : `€${v}`
                    }
                  />
                  <RechartsTooltip
                    formatter={(value, name) => [
                      formatMoney(value),
                      name === "revenue"
                        ? "Revenue"
                        : name === "expenses"
                        ? "Expenses"
                        : "Net Profit",
                    ]}
                    contentStyle={{
                      borderRadius: 8,
                      border: "none",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Legend
                    formatter={(value) =>
                      value === "revenue"
                        ? "Revenue"
                        : value === "expenses"
                        ? "Expenses"
                        : "Net Profit"
                    }
                  />
                  <Bar
                    dataKey="revenue"
                    fill={theme.palette.success.main}
                    radius={[4, 4, 0, 0]}
                    barSize={30}
                  />
                  <Bar
                    dataKey="expenses"
                    fill={theme.palette.warning.main}
                    radius={[4, 4, 0, 0]}
                    barSize={30}
                  />
                  <Line
                    type="monotone"
                    dataKey="netProfit"
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    dot={{ fill: theme.palette.primary.main, r: 4 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>

              {/* Monthly Summary Table */}
              <Box sx={{ mt: 3 }}>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Month</TableCell>
                        <TableCell align="right">Revenue</TableCell>
                        <TableCell align="right">Expenses</TableCell>
                        <TableCell align="right">Net Profit</TableCell>
                        <TableCell align="right">Margin</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accountsData.monthlyBreakdown.map((row) => (
                        <TableRow key={row.month} hover>
                          <TableCell>{row.month}</TableCell>
                          <TableCell align="right">
                            <Typography color="success.main" fontWeight={500}>
                              {formatMoney(row.revenue)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography color="warning.main" fontWeight={500}>
                              {formatMoney(row.expenses)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              color={
                                row.netProfit >= 0
                                  ? "success.main"
                                  : "error.main"
                              }
                              fontWeight={600}
                            >
                              {formatMoney(row.netProfit)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${
                                row.revenue > 0
                                  ? (
                                      (row.netProfit / row.revenue) *
                                      100
                                    ).toFixed(0)
                                  : 0
                              }%`}
                              size="small"
                              color={row.netProfit >= 0 ? "success" : "error"}
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          )}

          {/* Profit & Loss Statement */}
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Profit & Loss Statement - {selectedYear}
            </Typography>
            <Divider sx={{ my: 2 }} />

            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, pl: 0 }}>
                      Revenue
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 600, color: "success.main" }}
                    >
                      {formatMoney(accountsData.profitAndLoss.revenue)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pl: 2 }}>Less: Cost of Sales</TableCell>
                    <TableCell align="right">
                      ({formatMoney(accountsData.profitAndLoss.costOfSales)})
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                  >
                    <TableCell sx={{ fontWeight: 600, pl: 0 }}>
                      Gross Profit
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatMoney(accountsData.profitAndLoss.grossProfit)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pl: 2 }}>
                      Less: Operating Expenses
                    </TableCell>
                    <TableCell align="right" sx={{ color: "error.main" }}>
                      ({formatMoney(accountsData.profitAndLoss.expenses)})
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pl: 2 }}>Less: Staff Costs</TableCell>
                    <TableCell align="right" sx={{ color: "error.main" }}>
                      ({formatMoney(accountsData.profitAndLoss.payroll)})
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                  >
                    <TableCell sx={{ fontWeight: 600, pl: 0 }}>
                      Net Profit Before Tax
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatMoney(
                        accountsData.profitAndLoss.netProfitBeforeTax
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pl: 2 }}>
                      Less: Corporation Tax (12.5%)
                    </TableCell>
                    <TableCell align="right" sx={{ color: "warning.main" }}>
                      ({formatMoney(accountsData.profitAndLoss.corporationTax)})
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}
                  >
                    <TableCell
                      sx={{ fontWeight: 700, pl: 0, fontSize: "1.1rem" }}
                    >
                      Net Profit After Tax
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: "success.main",
                      }}
                    >
                      {formatMoney(
                        accountsData.profitAndLoss.netProfitAfterTax
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Expense Breakdown */}
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Expense Breakdown by Category
            </Typography>
            <Divider sx={{ my: 2 }} />

            {accountsData.expenseBreakdown?.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>
                        Items
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Gross
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        VAT
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Net (Deductible)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {accountsData.expenseBreakdown.map((expense) => (
                      <TableRow key={expense.category}>
                        <TableCell>{expense.category}</TableCell>
                        <TableCell align="center">
                          <Chip label={expense.count} size="small" />
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: "text.secondary" }}
                        >
                          {formatMoney(expense.grossTotal || expense.total)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: "text.secondary" }}
                        >
                          {formatMoney(expense.vat)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatMoney(expense.netTotal || expense.total)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={accountsData.expenseBreakdown.reduce(
                            (sum, e) => sum + e.count,
                            0
                          )}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, color: "text.secondary" }}
                      >
                        {formatMoney(
                          accountsData.expenseBreakdown.reduce(
                            (sum, e) => sum + (e.grossTotal || e.total),
                            0
                          )
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, color: "text.secondary" }}
                      >
                        {formatMoney(accountsData.vatSummary?.vatPaid || 0)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatMoney(accountsData.profitAndLoss?.expenses || 0)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No expenses recorded for this period.
                </Typography>
              </Box>
            )}
          </Paper>

          {/* VAT Summary */}
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              VAT Summary
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    VAT Collected
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color="success.main"
                  >
                    {formatMoney(accountsData.vatSummary.vatCollected)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    VAT Paid
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="error.main">
                    {formatMoney(accountsData.vatSummary.vatPaid)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    Net VAT{" "}
                    {accountsData.vatSummary.vatLiability >= 0
                      ? "Payable"
                      : "Refundable"}
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color={
                      accountsData.vatSummary.vatLiability >= 0
                        ? "warning.main"
                        : "success.main"
                    }
                  >
                    {formatMoney(
                      Math.abs(accountsData.vatSummary.vatLiability)
                    )}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Key Business Metrics */}
          {accountsData.metrics && (
            <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 2 }}
              >
                <MetricsIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Key Business Metrics
                </Typography>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={3}>
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                    }}
                  >
                    <PercentIcon sx={{ color: "success.main", mb: 1 }} />
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      color="success.main"
                    >
                      {accountsData.metrics.profitMargin}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Profit Margin
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 2,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                    }}
                  >
                    <ExpenseIcon sx={{ color: "warning.main", mb: 1 }} />
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      color="warning.main"
                    >
                      {accountsData.metrics.expenseRatio}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Expense Ratio
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                    }}
                  >
                    <InvoiceIcon sx={{ color: "info.main", mb: 1 }} />
                    <Typography variant="h4" fontWeight={700} color="info.main">
                      {formatMoney(accountsData.metrics.averageInvoiceValue)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Avg Invoice Value
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 2,
                      bgcolor: alpha(
                        accountsData.metrics.collectionRate >= 80
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                        0.1
                      ),
                    }}
                  >
                    <CheckIcon
                      sx={{
                        color:
                          accountsData.metrics.collectionRate >= 80
                            ? "success.main"
                            : "error.main",
                        mb: 1,
                      }}
                    />
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      color={
                        accountsData.metrics.collectionRate >= 80
                          ? "success.main"
                          : "error.main"
                      }
                    >
                      {accountsData.metrics.collectionRate}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Collection Rate
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Outstanding Invoices (Aging Report) */}
          {accountsData.unpaidInvoices?.count > 0 && (
            <Paper
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <TimeIcon color="warning" />
                  <Typography variant="h6" fontWeight={700}>
                    Outstanding Invoices
                  </Typography>
                  <Chip
                    label={`${accountsData.unpaidInvoices.count} unpaid`}
                    size="small"
                    color="warning"
                  />
                </Stack>
                <Typography variant="h6" fontWeight={700} color="warning.main">
                  {formatMoney(accountsData.unpaidInvoices.total)}
                </Typography>
              </Stack>

              {/* Aging Summary */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { label: "Current", key: "current", color: "success" },
                  { label: "1-30 Days", key: "1-30", color: "info" },
                  { label: "31-60 Days", key: "31-60", color: "warning" },
                  { label: "61-90 Days", key: "61-90", color: "error" },
                  { label: "90+ Days", key: "90+", color: "error" },
                ].map((bucket) => (
                  <Grid item xs={6} sm={2.4} key={bucket.key}>
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette[bucket.color].main, 0.1),
                      }}
                    >
                      <Typography variant="h6" fontWeight={700}>
                        {formatMoney(
                          accountsData.unpaidInvoices.aging[bucket.key] || 0
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {bucket.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Unpaid Invoice List */}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="center">Days Overdue</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {accountsData.unpaidInvoices.list
                      .slice(0, 10)
                      .map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {inv.invoiceNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>{inv.clientName || "—"}</TableCell>
                          <TableCell>
                            {new Date(inv.date).toLocaleDateString("en-IE", {
                              day: "numeric",
                              month: "short",
                            })}
                          </TableCell>
                          <TableCell>
                            {inv.dueDate
                              ? new Date(inv.dueDate).toLocaleDateString(
                                  "en-IE",
                                  { day: "numeric", month: "short" }
                                )
                              : "—"}
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={600}>
                              {formatMoney(inv.grossAmount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={
                                inv.daysOverdue > 0
                                  ? `${inv.daysOverdue} days`
                                  : "Not due"
                              }
                              size="small"
                              color={
                                inv.daysOverdue === 0
                                  ? "success"
                                  : inv.daysOverdue <= 30
                                  ? "warning"
                                  : "error"
                              }
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {accountsData.unpaidInvoices.list.length > 10 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1, textAlign: "center" }}
                >
                  Showing 10 of {accountsData.unpaidInvoices.list.length} unpaid
                  invoices
                </Typography>
              )}
            </Paper>
          )}

          {/* Currency Migration Alert */}
          {accountsData.transactions?.sales?.some(
            (inv) =>
              inv.originalCurrency &&
              inv.originalCurrency !== "EUR" &&
              !inv.exchangeRate
          ) && (
            <Alert
              severity="warning"
              sx={{ mb: 4, borderRadius: 2 }}
              action={
                <Button
                  color="warning"
                  size="small"
                  onClick={migrateCurrencies}
                  disabled={migrating}
                  startIcon={
                    migrating ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SyncIcon />
                    )
                  }
                >
                  {migrating ? "Converting..." : "Convert Now"}
                </Button>
              }
            >
              <AlertTitle>Foreign Currency Invoices Need Conversion</AlertTitle>
              Some invoices are in foreign currencies but don't have EUR
              equivalents stored. This affects your accounts accuracy. Click
              "Convert Now" to fetch historical exchange rates and update your
              invoices.
            </Alert>
          )}

          {/* Migration Result */}
          {migrationResult && (
            <Alert
              severity={migrationResult.success ? "success" : "error"}
              sx={{ mb: 4, borderRadius: 2 }}
              onClose={() => setMigrationResult(null)}
            >
              {migrationResult.success ? (
                <>
                  <AlertTitle>Currency Conversion Complete</AlertTitle>
                  {migrationResult.migrated} invoice(s) converted to EUR.
                  {migrationResult.invoices?.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {migrationResult.invoices.map((inv) => (
                        <Typography
                          key={inv.id}
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {inv.invoiceNumber}: {inv.originalCurrency}{" "}
                          {inv.originalAmount.toFixed(2)} → €
                          {inv.eurAmount.toFixed(2)} (rate:{" "}
                          {inv.exchangeRate.toFixed(4)})
                        </Typography>
                      ))}
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <AlertTitle>Conversion Failed</AlertTitle>
                  {migrationResult.error}
                </>
              )}
            </Alert>
          )}

          {/* Invoice Breakdown */}
          {accountsData.transactions?.sales?.length > 0 && (
            <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <InvoiceIcon color="success" />
                  <Typography variant="h6" fontWeight={700}>
                    Invoice Breakdown - {selectedYear}
                  </Typography>
                </Stack>
                <Chip
                  label={`${accountsData.transactions.sales.length} invoices`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              </Stack>

              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Net</TableCell>
                      <TableCell align="right">VAT</TableCell>
                      <TableCell align="right">Gross</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {accountsData.transactions.sales.map((inv) => (
                      <TableRow key={inv.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {inv.invoiceNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {inv.clientName || inv.description || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {new Date(inv.date).toLocaleDateString("en-IE", {
                            day: "numeric",
                            month: "short",
                            year: "2-digit",
                          })}
                        </TableCell>
                        <TableCell align="right">
                          {formatMoney(inv.netAmount)}
                        </TableCell>
                        <TableCell align="right">
                          {formatMoney(inv.vatAmount)}
                        </TableCell>
                        <TableCell align="right">
                          {inv.originalCurrency &&
                          inv.originalCurrency !== "EUR" ? (
                            <Tooltip
                              title={`Original: ${inv.originalCurrency} ${
                                inv.originalGrossAmount?.toFixed(2) || "—"
                              } @ ${inv.exchangeRate?.toFixed(4) || "N/A"}`}
                            >
                              <Typography
                                fontWeight={600}
                                sx={{ cursor: "help" }}
                              >
                                {formatMoney(inv.grossAmount)}
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: 0.5 }}
                                >
                                  ({inv.originalCurrency})
                                </Typography>
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography fontWeight={600}>
                              {formatMoney(inv.grossAmount)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={inv.status}
                            size="small"
                            color={
                              inv.status === "paid" ? "success" : "warning"
                            }
                            sx={{ textTransform: "capitalize" }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Invoice Totals */}
              <Box
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      Total Net
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {formatMoney(
                        accountsData.transactions.sales.reduce(
                          (sum, i) => sum + i.netAmount,
                          0
                        )
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      Total VAT Collected
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {formatMoney(
                        accountsData.transactions.sales.reduce(
                          (sum, i) => sum + i.vatAmount,
                          0
                        )
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      Total Gross
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      color="success.main"
                    >
                      {formatMoney(
                        accountsData.transactions.sales.reduce(
                          (sum, i) => sum + i.grossAmount,
                          0
                        )
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          )}

          {/* Detailed Explanations */}
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <LightbulbIcon color="warning" />
              <Typography variant="h6" fontWeight={700}>
                How These Accounts Were Calculated
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Detailed explanations for each section to help you understand your
              accounts and answer any Revenue queries.
            </Typography>

            {Object.entries(accountsData.explanations).map(([key, section]) => (
              <Accordion key={key} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CheckIcon color="success" fontSize="small" />
                    <Typography fontWeight={600}>{section.title}</Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {section.description}
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {section.details.map((detail, idx) => (
                      <Typography
                        component="li"
                        key={idx}
                        variant="body2"
                        sx={{ mb: 0.5 }}
                      >
                        {detail}
                      </Typography>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Paper>

          {/* Accountant Advice Section */}
          {accountsData.accountantAdvice?.length > 0 && (
            <Paper
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.warning.main,
                  0.08
                )} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 3,
                }}
              >
                <TipsIcon sx={{ color: "warning.main" }} />
                <Typography variant="h6" fontWeight={700}>
                  Accountant's Advice & Recommendations
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {accountsData.accountantAdvice.map((item, idx) => {
                  const getAdviceIcon = (icon) => {
                    switch (icon) {
                      case "tax":
                        return <TaxIcon />;
                      case "savings":
                        return <SavingsIcon />;
                      case "calendar":
                        return <CalendarIcon />;
                      case "money":
                        return <MoneyIcon />;
                      case "warning":
                        return <WarningIcon />;
                      case "check":
                        return <ComplianceIcon />;
                      case "chart":
                        return <RevenueIcon />;
                      case "vat":
                        return <ReceiptIcon />;
                      case "info":
                      default:
                        return <LightbulbIcon />;
                    }
                  };

                  const getPriorityColor = (priority) => {
                    switch (priority) {
                      case "high":
                        return "error";
                      case "medium":
                        return "warning";
                      case "low":
                      default:
                        return "info";
                    }
                  };

                  return (
                    <Grid item xs={12} md={6} key={idx}>
                      <Card
                        sx={{
                          height: "100%",
                          borderRadius: 2,
                          borderLeft: `4px solid`,
                          borderLeftColor: `${getPriorityColor(
                            item.priority
                          )}.main`,
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1.5,
                              mb: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                color: `${getPriorityColor(
                                  item.priority
                                )}.main`,
                              }}
                            >
                              {getAdviceIcon(item.icon)}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mb: 0.5,
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={600}
                                >
                                  {item.title}
                                </Typography>
                                <Chip
                                  label={item.category}
                                  size="small"
                                  color={getPriorityColor(item.priority)}
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: "0.7rem" }}
                                />
                              </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1.5 }}
                              >
                                {item.description}
                              </Typography>
                            </Box>
                          </Box>

                          {item.actionItems?.length > 0 && (
                            <Box sx={{ pl: 4, mb: 1.5 }}>
                              {item.actionItems.map((action, actionIdx) => (
                                <Box
                                  key={actionIdx}
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                    mb: 0.5,
                                  }}
                                >
                                  <CheckIcon
                                    sx={{
                                      fontSize: 14,
                                      mt: 0.3,
                                      color: "success.main",
                                    }}
                                  />
                                  <Typography variant="body2">
                                    {action}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {item.tip && (
                            <Box
                              sx={{
                                p: 1.5,
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                borderRadius: 1,
                                ml: 4,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 0.5,
                                }}
                              >
                                <LightbulbIcon
                                  sx={{ fontSize: 14, color: "info.main" }}
                                />
                                <span>
                                  <strong>Tip:</strong> {item.tip}
                                </span>
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          )}

          {/* Actions */}
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ sm: "center" }}
            >
              <Box>
                <Typography fontWeight={600}>Ready to file?</Typography>
                <Typography variant="body2" color="text.secondary">
                  Download your accounts or send to your accountant for review.
                </Typography>
              </Box>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    // TODO: Implement PDF download
                    alert("PDF download coming soon!");
                  }}
                >
                  Download PDF
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    window.open("https://www.ros.ie", "_blank");
                  }}
                >
                  Go to ROS →
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </>
      )}

      {/* Empty State */}
      {!accountsData && !loading && (
        <Paper
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: 3,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <TaxIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Generate Your Annual Accounts
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Select a financial year and click "Generate Accounts" to create your
            Revenue-ready financial statements with detailed explanations.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Includes: Profit & Loss Statement • Expense Breakdown • Corporation
            Tax Computation • VAT Summary
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
